import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Footer from "@/components/Footer";
import TrinityMotif from "@/components/TrinityMotif";
import { Button } from "@/components/ui/button";
import {
  Brain, ClipboardCheck, ShieldCheck, ArrowRight, Check, Users, Clock, GitBranch,
  Radar, ScanLine, FileCode2, PlayCircle, RefreshCw, Cloud, Search, FileLock2, ListChecks, BadgeCheck,
} from "lucide-react";

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.5, delay: i * 0.08 } }),
};

const MODEL = [
  { n: "01", title: "Think", icon: Brain, color: "#3A2DB8",
    desc: "Understand changes, map business impact, identify risk, and determine the right coverage before execution begins.",
    points: ["Release impact analysis", "Process-risk mapping", "Coverage recommendations"] },
  { n: "02", title: "Test", icon: ClipboardCheck, color: "#7C3AED",
    desc: "Create and maintain test cases, orchestrate regression, capture evidence and record defects across your landscape.",
    points: ["AI-assisted test design", "Automated execution", "Evidence and traceability"] },
  { n: "03", title: "Trust", icon: ShieldCheck, color: "#FF6B3D",
    desc: "Translate testing activity into clear release intelligence while accountable people retain control.",
    points: ["Defect intelligence", "Release readiness", "Human approval controls"] },
];

const PIPELINE = [
  { icon: ScanLine, t: "Scan", d: "Agents crawl your SAP tenant URL and map every clickable feature." },
  { icon: FileCode2, t: "Generate", d: "AI builds executable regression test scripts per module." },
  { icon: PlayCircle, t: "Execute", d: "Run against your encrypted test data or auto-generated dummy data." },
  { icon: RefreshCw, t: "Adapt", d: "Track SAP releases, rebuild impacted scripts, re-run automatically." },
];

const SF_MODULES = ["Employee Central", "Recruitment & Onboarding", "Time, Leave & Payroll",
  "Performance & Goals", "Compensation", "Integrations & Permissions"];
const PC_MODULES = ["Order to Cash", "Procure to Pay", "Finance & GL",
  "Master Data", "Inventory & Logistics", "Integrations"];

const Callout = ({ icon: Icon, a, b, testId }) => (
  <div className="flex items-start gap-3 callout-line pb-1 w-fit" data-testid={testId}>
    <span className="w-11 h-11 rounded-lg border-2 border-[#2A1E9E]/70 bg-white/80 flex items-center justify-center shrink-0 relative">
      <Icon className="w-5 h-5 text-[#2A1E9E]" strokeWidth={2} />
      <span className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-[#FF6B3D] border-2 border-white" />
    </span>
    <span className="font-heading font-semibold text-[#2A1E9E] leading-tight text-base">{a}<br />{b}</span>
  </div>
);

export default function Landing() {
  const [packages, setPackages] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/packages").then((r) => setPackages(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 grid-lines opacity-70" aria-hidden="true" />
        <div className="absolute left-0 right-0 top-[38%] lg:top-[45%] h-14 data-wave opacity-30 -rotate-2 pointer-events-none hidden lg:block" aria-hidden="true" />
        <div className="relative max-w-[1400px] mx-auto px-6 pt-16 pb-12 lg:pt-20">
          <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr_0.75fr] gap-10 items-center">
            {/* left copy */}
            <div>
              <motion.p variants={fade} initial="hidden" animate="show" className="overline text-[#2A1E9E] mb-4" data-testid="hero-overline">
                Specia AI TestAgents as a Service
              </motion.p>
              <motion.h1 variants={fade} initial="hidden" animate="show" custom={1}
                className="neon-word text-5xl sm:text-6xl lg:text-7xl leading-none" data-testid="hero-title">
                TRINITY
              </motion.h1>
              <motion.p variants={fade} initial="hidden" animate="show" custom={2}
                className="mt-6 font-heading font-semibold text-xl sm:text-2xl text-[#1E1560] leading-snug max-w-lg" data-testid="hero-subtitle">
                Continuous Testing Intelligence for<br className="hidden sm:block" /> SAP SuccessFactors &amp; SAP Public Cloud
              </motion.p>
              <motion.div variants={fade} initial="hidden" animate="show" custom={3} className="mt-7 flex flex-wrap gap-3">
                <span className="pill-badge" data-testid="badge-sf"><Users className="w-4 h-4 text-[#3A2DB8]" /> SAP SuccessFactors</span>
                <span className="pill-badge" data-testid="badge-pc"><Cloud className="w-4 h-4 text-[#3A2DB8]" /> SAP Public Cloud</span>
              </motion.div>
              <motion.div variants={fade} initial="hidden" animate="show" custom={4} className="mt-10">
                <Callout icon={FileLock2} a="Generate &" b="Maintain Test Cases" testId="callout-generate" />
              </motion.div>
              <motion.div variants={fade} initial="hidden" animate="show" custom={5} className="mt-12 flex flex-wrap gap-3">
                <Button onClick={() => navigate("/register")} data-testid="hero-get-started-btn"
                  className="rounded-full h-12 px-7 bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold text-base gap-2 glow-primary">
                  Deploy your agents <ArrowRight className="w-4 h-4" />
                </Button>
                <Button variant="outline" onClick={() => document.getElementById("model")?.scrollIntoView()}
                  data-testid="hero-see-model-btn" className="rounded-full h-12 px-7 border-[#3A2DB8]/30 text-[#2A1E9E] bg-white/70 hover:bg-white text-base">
                  See how Trinity works
                </Button>
              </motion.div>
            </div>

            {/* motif */}
            <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
              className="mx-auto w-full max-w-[520px] float-slow">
              <TrinityMotif className="w-full h-auto drop-shadow-[0_30px_60px_rgba(58,45,184,0.25)]" />
            </motion.div>

            {/* right callouts */}
            <motion.div variants={fade} initial="hidden" animate="show" custom={3}
              className="flex flex-row lg:flex-col flex-wrap gap-10 lg:gap-14 justify-center lg:justify-between lg:h-full lg:py-6">
              <Callout icon={Search} a="Understand" b="System Changes" testId="callout-understand" />
              <Callout icon={ListChecks} a="Execute" b="Regression Testing" testId="callout-execute" />
              <Callout icon={BadgeCheck} a="Validate" b="Every Release" testId="callout-validate" />
            </motion.div>
          </div>

          {/* tagline */}
          <motion.div variants={fade} initial="hidden" animate="show" custom={6} className="mt-16 text-center relative z-10">
            <h2 className="font-heading font-bold text-2xl sm:text-3xl xl:text-[2.6rem] tracking-tight leading-tight" data-testid="hero-tagline">
              <span className="text-[#2A1E9E]">Continuous Intelligence. </span>
              <span className="text-[#7C3AED]">Continuous Testing. </span>
              <span className="gradient-text">Continuous Assurance.</span>
            </h2>
            <div className="mx-auto mt-5 h-px w-64 bg-gradient-to-r from-transparent via-[#FF6B3D] to-transparent" />
            <p className="mt-5 font-heading font-semibold text-lg sm:text-xl text-[#1E1560]">AI-Powered Testing. Human-Governed Assurance.</p>
            <a href="https://trinity.platform.specia.ai" className="inline-block mt-6 rounded-full bg-[#2A1E9E] text-white font-heading font-semibold px-8 py-2.5 glow-primary hover:bg-[#3A2DB8] transition-colors"
              data-testid="hero-domain-pill">trinity.platform.specia.ai</a>
          </motion.div>
        </div>
      </section>

      {/* MODEL */}
      <section id="model" className="max-w-[1400px] mx-auto px-6 py-24">
        <p className="overline text-[#7C3AED] mb-3">The Trinity Model</p>
        <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl mb-14 text-[#1E1560]">Think. Test. Trust.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {MODEL.map((m, i) => (
            <motion.div key={m.n} variants={fade} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
              className="hover-lift card-soft p-8 relative">
              <span className="absolute top-6 right-6 font-mono text-xs text-secondary">{m.n}</span>
              <span className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: `${m.color}1A` }}>
                <m.icon className="w-6 h-6" style={{ color: m.color }} strokeWidth={1.8} />
              </span>
              <h3 className="font-display font-bold text-xl tracking-wider mb-3 uppercase" style={{ color: m.color }}>{m.title}</h3>
              <p className="text-secondary text-sm leading-relaxed mb-6">{m.desc}</p>
              <ul className="space-y-2">
                {m.points.map((p) => (
                  <li key={p} className="flex items-center gap-2 text-sm text-[#1E1560]">
                    <Check className="w-4 h-4" style={{ color: m.color }} /> {p}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* PIPELINE */}
      <section className="border-y border-border bg-white/60">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <div className="flex items-center gap-3 mb-3">
            <Radar className="w-5 h-5 text-[#FF6B3D]" />
            <p className="overline text-[#7C3AED]">How the agents work</p>
          </div>
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl mb-14 max-w-2xl text-[#1E1560]">
            Autonomous scan, generate, execute — then adapt to every SAP release.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {PIPELINE.map((s, i) => (
              <div key={s.t} className="card-soft p-8 hover-lift">
                <div className="flex items-center justify-between mb-6">
                  <s.icon className="w-8 h-8 text-[#3A2DB8]" strokeWidth={1.8} />
                  <span className="font-mono text-xs text-[#FF6B3D]">0{i + 1}</span>
                </div>
                <h4 className="font-heading font-semibold text-lg mb-2 text-[#1E1560]">{s.t}</h4>
                <p className="text-secondary text-sm leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLATFORMS */}
      <section id="platforms" className="max-w-[1400px] mx-auto px-6 py-24">
        <p className="overline text-[#7C3AED] mb-3">Coverage</p>
        <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl mb-14 text-[#1E1560]">Built for your SAP landscape.</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[{ title: "SAP SuccessFactors", tag: "Continuous HXM Assurance", color: "#3A2DB8", icon: Users, mods: SF_MODULES },
            { title: "SAP Public Cloud", tag: "End-to-End ERP Assurance", color: "#FF6B3D", icon: Cloud, mods: PC_MODULES }].map((p) => (
            <div key={p.title} className="card-soft p-8">
              <div className="flex items-center gap-3 mb-6">
                <span className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: `${p.color}1A` }}>
                  <p.icon className="w-5 h-5" style={{ color: p.color }} />
                </span>
                <p className="overline text-secondary">{p.tag}</p>
              </div>
              <h3 className="font-heading font-bold text-2xl mb-6 text-[#1E1560]">{p.title}</h3>
              <div className="grid grid-cols-2 gap-2">
                {p.mods.map((m) => (
                  <div key={m} className="rounded-lg border border-border bg-background/70 p-3.5 text-sm text-[#1E1560] hover:border-[#7C3AED]/40 transition-colors">{m}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING PREVIEW */}
      <section className="border-y border-border bg-white/60">
        <div className="max-w-[1400px] mx-auto px-6 py-24">
          <p className="overline text-[#7C3AED] mb-3">Pricing</p>
          <h2 className="font-heading font-bold tracking-tight text-3xl sm:text-4xl mb-14 text-[#1E1560]">Choose your testing package</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
            {packages.map((pkg) => (
              <div key={pkg.id} className="card-soft p-8 hover-lift" data-testid={`landing-pkg-${pkg.id}`}>
                <p className="font-mono text-xs text-[#FF6B3D] mb-4">{pkg.code}</p>
                <h3 className="font-heading font-bold text-2xl text-[#1E1560]">{pkg.name}</h3>
                <p className="text-secondary text-sm mb-6">{pkg.tagline}</p>
                <div className="font-heading font-bold text-4xl mb-1 text-[#2A1E9E]">${pkg.monthly_price.toLocaleString()}
                  <span className="text-base text-secondary font-normal">/month</span></div>
                <p className="text-xs text-secondary mb-6">+ one-time onboarding · <a href="/#contact" className="text-[#3A2DB8] hover:text-[#FF6B3D] font-medium">talk to us for a quote</a></p>
                <ul className="space-y-2 mb-8">
                  {pkg.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-[#1E1560]"><Check className="w-4 h-4 text-[#7C3AED]" /> {f}</li>
                  ))}
                </ul>
                <Button onClick={() => navigate("/register")} className="rounded-full w-full bg-[#2A1E9E] hover:bg-[#3A2DB8] text-white font-semibold"
                  data-testid={`landing-buy-${pkg.id}`}>Buy package</Button>
              </div>
            ))}
          </div>
          <Link to="/pricing" className="inline-flex items-center gap-2 mt-8 text-[#3A2DB8] hover:text-[#FF6B3D] text-sm font-semibold transition-colors" data-testid="view-all-packages">
            View full pricing details <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="max-w-[1400px] mx-auto px-6 py-24">
        <div className="border-l-4 border-[#FF6B3D] pl-8 max-w-3xl">
          <p className="font-heading font-bold text-2xl sm:text-3xl leading-tight tracking-tight text-[#1E1560]">
            “Specia Trinity is developing autonomous AI testing agents that help SAP teams think smarter,
            test faster, and release with confidence.”
          </p>
          <div className="mt-6 flex items-center gap-3 text-sm text-secondary">
            <Users className="w-4 h-4 text-[#7C3AED]" /> Specia-Trinity — AI Agent at Spectar
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-border bg-[#2A1E9E] text-white">
        <div className="max-w-[1400px] mx-auto px-6 py-24 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <h2 className="font-heading font-bold tracking-tight text-4xl sm:text-5xl max-w-2xl">Ready to ship with confidence?</h2>
            <p className="text-white/70 mt-4 flex items-center gap-2"><Clock className="w-4 h-4 text-[#FF6B3D]" /> Deploy your first testing agent in minutes.</p>
          </div>
          <Button onClick={() => navigate("/register")} data-testid="cta-get-started-btn"
            className="rounded-full h-14 px-8 bg-[#FF6B3D] hover:bg-[#F4511E] text-white font-semibold text-base gap-2 glow-coral shrink-0">
            <GitBranch className="w-5 h-5" /> Get started free
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
